import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://csksportswear.in/backend/api';

function Inventory() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageRef = useRef(null);
  const [form, setForm] = useState({
    name: '', category_id: '', sku: '', price: '',
    cost_price: '', stock: '', min_stock: '5',
    description: '', image: ''
  });
  const navigate = useNavigate();
  const token = localStorage.getItem('csk_token');
  const user = JSON.parse(localStorage.getItem('csk_user') || '{}');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/inventory.php?action=list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setProducts(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/products.php?action=categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setCategories(res.data.data);
    } catch (err) { console.error(err); }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    const tok = localStorage.getItem('csk_token');
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API}/upload.php?action=image`, formData, {
        headers: { Authorization: `Bearer ${tok}` }
      });
      if (res.data.success) {
        setForm(prev => ({ ...prev, image: res.data.url }));
        setImagePreview(res.data.url);
      }
    } catch (err) {
      alert('Image upload failed!');
    } finally { setUploadingImage(false); }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.price) { alert('Name and price required!'); return; }
    try {
      if (editProduct) {
        await axios.post(`${API}/products.php?action=update`, { ...form, id: editProduct.id }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/products.php?action=create`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditProduct(null);
      setImagePreview(null);
      setForm({ name: '', category_id: '', sku: '', price: '', cost_price: '', stock: '', min_stock: '5', description: '', image: '' });
      fetchProducts();
    } catch (err) { alert('Error saving product!'); }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setForm({
      name: product.name, category_id: product.category_id,
      sku: product.sku || '', price: product.price,
      cost_price: product.cost_price || '', stock: product.stock,
      min_stock: product.min_stock, description: product.description || '',
      image: product.image || ''
    });
    setImagePreview(product.image || null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.get(`${API}/products.php?action=delete&id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (err) { alert('Error!'); }
  };

  const handleStockUpdate = async (id, type) => {
    const qty = prompt(`Enter quantity to ${type}:`);
    if (!qty || isNaN(qty)) return;
    try {
      await axios.post(`${API}/inventory.php?action=update_stock`, {
        product_id: id, quantity: parseInt(qty), type
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchProducts();
    } catch (err) { alert('Error!'); }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku && p.sku.toLowerCase().includes(search.toLowerCase()))
  );

  const inputStyle = {
    width: '100%', padding: '10px 14px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: 'white',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '🏠' },
    { path: '/admin/inventory', label: 'Inventory', icon: '📦' },
    ...(user.role === 'admin' ? [{ path: '/admin/pos', label: 'POS Billing', icon: '🧾' }] : []),
    { path: '/admin/reports', label: 'Reports', icon: '📊' },
    { path: '/admin/gallery', label: 'Gallery', icon: '🖼️' },
    ...(user.role === 'admin' ? [
      { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
      { path: '/admin/users', label: 'Users', icon: '👥' },
    ] : []),
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0A0A0A', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .inv-sidebar { width: 200px; background: #000; border-right: 1px solid rgba(255,255,255,0.08); display: flex; flex-direction: column; position: fixed; top: 0; left: 0; bottom: 0; z-index: 100; transition: transform 0.3s ease; }
        .inv-main { margin-left: 200px; flex: 1; padding: 32px; }
        .inv-mobile-hdr { display: none; background: #000; padding: 12px 16px; position: fixed; top: 0; left: 0; right: 0; z-index: 99; border-bottom: 1px solid rgba(255,255,255,0.08); align-items: center; justify-content: space-between; }
        .inv-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 99; }
        @media (max-width: 768px) {
          .inv-sidebar { transform: translateX(-100%); }
          .inv-sidebar.open { transform: translateX(0); }
          .inv-main { margin-left: 0; padding: 72px 16px 16px; }
          .inv-mobile-hdr { display: flex; }
          .inv-overlay.show { display: block; }
          .inv-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
        }
        .inv-nav-link { display: flex; align-items: center; gap: 10px; padding: 12px 16px; color: #475569; font-size: 13px; font-weight: 500; text-decoration: none; border-left: 3px solid transparent; transition: all 0.2s; }
        .inv-nav-link:hover { color: white; background: rgba(255,255,255,0.05); }
        .inv-nav-link.active { color: white; background: rgba(0,102,255,0.1); border-left-color: #0066FF; }
        .img-upload-zone { border: 2px dashed rgba(0,102,255,0.3); border-radius: 10px; padding: 20px; text-align: center; cursor: pointer; transition: all 0.2s; background: rgba(0,102,255,0.02); }
        .img-upload-zone:hover { border-color: #0066FF; background: rgba(0,102,255,0.05); }
      `}</style>

      {/* Mobile Header */}
      <div className="inv-mobile-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
          <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 800, color: 'white' }}>CSK</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer' }}>☰</button>
      </div>

      <div className={`inv-overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <div className={`inv-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
            <div>
              <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 800, letterSpacing: '2px', color: 'white' }}>CSK</div>
              <div style={{ fontSize: '8px', color: '#475569', letterSpacing: '1px' }}>ADMIN PANEL</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>{user.name}</div>
          <div style={{ fontSize: '11px', color: '#0066FF', textTransform: 'uppercase', letterSpacing: '1px' }}>{user.role}</div>
        </div>
        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {menuItems.map(item => (
            <Link key={item.path} to={item.path}
              className={`inv-nav-link ${window.location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}>
              <span>{item.icon}</span>{item.label}
            </Link>
          ))}
        </nav>
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={() => { localStorage.clear(); navigate('/login'); }} style={{ width: '100%', padding: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>🚪 Logout</button>
        </div>
      </div>

      {/* Main */}
      <div className="inv-main">
        <div className="inv-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontFamily: 'Montserrat', fontSize: '28px', fontWeight: 800, color: 'white' }}>Inventory</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Manage products and stock</p>
          </div>
          <button onClick={() => {
            setEditProduct(null);
            setImagePreview(null);
            setForm({ name: '', category_id: '', sku: '', price: '', cost_price: '', stock: '', min_stock: '5', description: '', image: '' });
            setShowModal(true);
          }} style={{ background: 'linear-gradient(135deg, #0066FF, #0052CC)', color: 'white', padding: '10px 24px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Add Product
          </button>
        </div>

        <input placeholder="🔍 Search products..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, marginBottom: '24px', maxWidth: '400px' }} />

        {loading ? (
          <div style={{ textAlign: 'center', color: '#475569', padding: '60px' }}>Loading...</div>
        ) : (
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 700, color: 'white' }}>Products</h3>
              <span style={{ color: '#475569', fontSize: '13px' }}>{filtered.length} products</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    {['Image', 'Product', 'Category', 'SKU', 'Price', 'Stock', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#475569' }}>No products found</td></tr>
                  ) : filtered.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', overflow: 'hidden', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {p.image ? (
                            <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
                          ) : (
                            <span style={{ fontSize: '20px' }}>👕</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 600, color: 'white' }}>{p.name}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#94A3B8' }}>{p.category_name}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#94A3B8' }}>{p.sku || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#0066FF', fontWeight: 700 }}>₹{p.price}</td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 700, color: 'white' }}>{p.stock}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 600, background: p.stock_status === 'in_stock' ? 'rgba(22,163,74,0.1)' : p.stock_status === 'low_stock' ? 'rgba(245,158,11,0.1)' : 'rgba(220,38,38,0.1)', color: p.stock_status === 'in_stock' ? '#16A34A' : p.stock_status === 'low_stock' ? '#F59E0B' : '#DC2626' }}>
                          {p.stock_status === 'in_stock' ? 'In Stock' : p.stock_status === 'low_stock' ? 'Low Stock' : 'Out of Stock'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button onClick={() => handleStockUpdate(p.id, 'add')} style={{ padding: '5px 10px', background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: '6px', color: '#16A34A', fontSize: '12px', cursor: 'pointer' }}>+Stock</button>
                          <button onClick={() => handleEdit(p)} style={{ padding: '5px 10px', background: 'rgba(0,102,255,0.1)', border: '1px solid rgba(0,102,255,0.3)', borderRadius: '6px', color: '#0066FF', fontSize: '12px', cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => handleDelete(p.id)} style={{ padding: '5px 10px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#DC2626', fontSize: '12px', cursor: 'pointer' }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: '#0D1B2A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: 800, marginBottom: '24px', color: 'white' }}>
              {editProduct ? 'Edit Product' : 'Add Product'}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Image Upload */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#94A3B8' }}>Product Image</label>

                {/* Preview */}
                {imagePreview ? (
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <img src={imagePreview} alt="preview" style={{ width: '100%', height: '180px', objectFit: 'contain', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} />
                    <button onClick={() => { setImagePreview(null); setForm(prev => ({ ...prev, image: '' })); }} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(220,38,38,0.9)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', color: 'white', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                ) : (
                  <div className="img-upload-zone" onClick={() => imageRef.current?.click()}>
                    {uploadingImage ? (
                      <div style={{ color: '#0066FF', fontWeight: 600, padding: '20px' }}>Uploading...</div>
                    ) : (
                      <>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
                        <div style={{ color: 'white', fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Click to upload image</div>
                        <div style={{ color: '#475569', fontSize: '12px' }}>JPG, PNG, WEBP — Max 5MB</div>
                      </>
                    )}
                    <input ref={imageRef} type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => {
                        if (e.target.files[0]) {
                          handleImageUpload(e.target.files[0]);
                          const r = new FileReader();
                          r.onload = ev => setImagePreview(ev.target.result);
                          r.readAsDataURL(e.target.files[0]);
                        }
                      }} />
                  </div>
                )}
              </div>

              {/* Product Name */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Product Name *</label>
                <input type="text" placeholder="Enter product name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
              </div>

              {/* Category */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Category</label>
                <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} style={{ ...inputStyle, background: '#1E293B' }}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* SKU */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>SKU</label>
                <input type="text" placeholder="Stock Keeping Unit" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} style={inputStyle} />
              </div>

              {/* Price + Cost */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Selling Price *</label>
                  <input type="number" placeholder="₹" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Cost Price</label>
                  <input type="number" placeholder="₹" value={form.cost_price} onChange={e => setForm({ ...form, cost_price: e.target.value })} style={inputStyle} />
                </div>
              </div>

              {/* Stock + Min Stock */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Stock Quantity</label>
                  <input type="number" placeholder="0" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Min Stock Alert</label>
                  <input type="number" placeholder="5" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: e.target.value })} style={inputStyle} />
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Description</label>
                <textarea rows={3} placeholder="Product description..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, resize: 'none', lineHeight: 1.6 }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={handleSubmit} disabled={uploadingImage} style={{ flex: 1, padding: '12px', background: uploadingImage ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #0066FF, #0052CC)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: uploadingImage ? 'not-allowed' : 'pointer' }}>
                {uploadingImage ? 'Uploading...' : editProduct ? 'Update Product' : 'Add Product'}
              </button>
              <button onClick={() => { setShowModal(false); setEditProduct(null); setImagePreview(null); }} style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Inventory;
